import type {
  CreditCardCockpitAccount,
  CreditCardCockpitResponse,
  InstallmentLadderEntry,
} from '@/lib/api';
import { adaptInvoiceToFatura } from '@/components/transactions/screen/adapters';
import type { ScreenFatura } from '@/components/transactions/screen/types';
import {
  calendarDateKey,
  compareCalendarDateValues,
  dateToCalendarKey,
  isOnOrAfterCalendarDate,
} from './dates';

export type AccountCard = {
  account: CreditCardCockpitAccount;
  faturas: ScreenFatura[];
  total: number;
  openTotal: number;
  nextDue: ScreenFatura | null;
};

export type DueSummary = {
  dueDate: string;
  fatura: ScreenFatura;
};

export type FaturasSummary = {
  total: number;
  openTotal: number;
  availableLimit: number;
  nextDue: DueSummary | null;
  /** Aggregate utilization across all cards with both limit and available set. Null when no card reports a limit. */
  limitUsedPct: number | null;
  /** Calendar days from `now` to the next due date. Negative values are clamped to 0. */
  nextDueDays: number | null;
  /** Display name of the card that owns `nextDue`. */
  nextDueAccountName: string | null;
  /** Accent color of the card that owns `nextDue`, for visual linkage to the per-card panel. */
  nextDueAccentColor: string | null;
  /** Number of distinct active (unpaid) cards — feeds the hero sub-line. */
  activeCardCount: number;
  /** Sum of faturas closed in the prior cycle (position === 'closing'). */
  priorPeriodTotal: number;
  /** Month-over-month delta of openTotal vs priorPeriodTotal, as a percentage. Null when prior is 0. */
  periodDeltaPct: number | null;
};

function faturaCycleEndSortDate(fatura: ScreenFatura): string | null | undefined {
  return fatura.source.cycleEnd ?? fatura.source.closeDate ?? fatura.source.dueDate;
}

function compareFaturaCycleEnd(a: ScreenFatura, b: ScreenFatura): number {
  return compareCalendarDateValues(faturaCycleEndSortDate(a), faturaCycleEndSortDate(b));
}

function compareFaturaCycleEndDesc(a: ScreenFatura, b: ScreenFatura): number {
  return compareFaturaCycleEnd(b, a);
}

function compareMaybeDate(a: string | null | undefined, b: string | null | undefined): number {
  return compareCalendarDateValues(a, b);
}

function hasUnpaidInvoice(card: AccountCard): boolean {
  return card.faturas.some((fatura) => fatura.status !== 'paid');
}

export function buildAccountCards(
  cockpit: CreditCardCockpitResponse | null,
  now: Date = new Date(),
): AccountCard[] {
  if (!cockpit) return [];
  return cockpit.accounts.map((account) => {
    const faturas = account.invoices
      .map((invoice) => adaptInvoiceToFatura(invoice, account, undefined, now))
      .sort(compareFaturaCycleEndDesc);
    const nextDue = faturas
      .filter((fatura) => fatura.status !== 'paid' && isOnOrAfterCalendarDate(fatura.dueDate, now))
      .sort((a, b) => compareMaybeDate(a.dueDate, b.dueDate))[0] ?? null;
    return {
      account,
      faturas,
      total: faturas.reduce((sum, fatura) => sum + fatura.total, 0),
      openTotal: faturas
        .filter((fatura) => fatura.status === 'open')
        .reduce((sum, fatura) => sum + fatura.total, 0),
      nextDue,
    };
  });
}

export function sortAccountCards(cards: AccountCard[]): AccountCard[] {
  return [...cards].sort((a, b) => {
    const aActive = hasUnpaidInvoice(a);
    const bActive = hasUnpaidInvoice(b);
    if (aActive !== bActive) return aActive ? -1 : 1;
    if (!aActive && !bActive) return b.total - a.total;

    const dueCompare = compareMaybeDate(a.nextDue?.dueDate, b.nextDue?.dueDate);
    if (dueCompare !== 0) return dueCompare;
    if (b.openTotal !== a.openTotal) return b.openTotal - a.openTotal;
    return a.account.name.localeCompare(b.account.name);
  });
}

function daysUntilCalendarDate(target: string | null | undefined, now: Date): number | null {
  const targetKey = calendarDateKey(target);
  if (!targetKey) return null;
  const nowKey = dateToCalendarKey(now);
  const [ty, tm, td] = targetKey.split('-').map(Number);
  const [ny, nm, nd] = nowKey.split('-').map(Number);
  const targetMs = Date.UTC(ty!, tm! - 1, td!);
  const nowMs = Date.UTC(ny!, nm! - 1, nd!);
  return Math.max(0, Math.round((targetMs - nowMs) / 86400000));
}

/** Mirrors the backend's SharedHelpers.MonthLabels ("Ago 26") for months the ladder doesn't carry. */
const SHORT_MONTH_LABELS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
] as const;

function shortMonthLabel(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  const label = SHORT_MONTH_LABELS[(monthNumber ?? 0) - 1];
  return label && year ? `${label} ${String(year).slice(2)}` : month;
}

/**
 * Data for the "Compromisso por mês" chart: the backend installment ladder
 * deliberately starts the month AFTER the latest open fatura (so future
 * parcelas never double-count the open cycle), which left the chart opening at
 * next month with the single biggest commitment — the current open fatura —
 * missing entirely. The chart must always LEAD with the open fatura(s): open
 * invoices grouped by due month come first, then the future-parcelas tail.
 */
export function buildCommitmentChartData(
  cockpit: CreditCardCockpitResponse | null,
): InstallmentLadderEntry[] {
  if (!cockpit) return [];
  const ladder = cockpit.installmentLadder ?? [];

  const openByMonth = new Map<string, number>();
  for (const account of cockpit.accounts) {
    for (const invoice of account.invoices) {
      // awaitingBillSync placeholders withhold projection everywhere else in the
      // UI ("Sincronizando") — keep them off the chart too.
      if (!invoice.isOpen || invoice.awaitingBillSync || !invoice.dueDate || invoice.total <= 0) {
        continue;
      }
      const month = invoice.dueDate.slice(0, 7);
      openByMonth.set(month, (openByMonth.get(month) ?? 0) + invoice.total);
    }
  }
  if (openByMonth.size === 0) return ladder;

  const merged = new Map<string, InstallmentLadderEntry>();
  for (const [month, amount] of openByMonth) {
    merged.set(month, { month, monthLabel: shortMonthLabel(month), amount });
  }
  // The global ladder starts after the LATEST open due month, so overlap can't
  // happen by construction — summing on collision is just a safety net.
  for (const entry of ladder) {
    const existing = merged.get(entry.month);
    merged.set(
      entry.month,
      existing ? { ...entry, amount: existing.amount + entry.amount } : entry,
    );
  }
  return [...merged.values()].sort((a, b) => a.month.localeCompare(b.month));
}

export function deriveFaturasSummary(
  cards: AccountCard[],
  now: Date = new Date(),
): FaturasSummary {
  const faturas = cards.flatMap((card) => card.faturas);
  const nextCard = cards
    .filter((card) => card.nextDue?.dueDate)
    .sort((a, b) => compareMaybeDate(a.nextDue?.dueDate, b.nextDue?.dueDate))[0] ?? null;

  // Usage is (bank-reported used) / (limit in effect). Deriving "used" as limit - available is
  // wrong whenever the cardholder capped their own limit in the bank's app: creditLimit is then
  // the APPROVED limit while creditAvailableLimit counts against the cap, and the subtraction
  // invents the gap between them as spend. Cards whose figures don't reconcile are skipped
  // entirely rather than dragging the aggregate off.
  let limitSum = 0;
  let usedSum = 0;
  let hasLimit = false;
  for (const card of cards) {
    const limit = card.account.creditEffectiveLimit;
    const used = card.account.creditUsedAmount;
    if (!card.account.creditLimitReliable || limit === null || used === null || limit <= 0) continue;
    hasLimit = true;
    limitSum += limit;
    usedSum += Math.max(0, used);
  }
  const limitUsedPct = hasLimit ? Math.min(1, usedSum / limitSum) : null;

  const openTotal = faturas
    .filter((fatura) => fatura.status === 'open')
    .reduce((sum, fatura) => sum + fatura.total, 0);

  const priorPeriodTotal = faturas
    .filter((fatura) => fatura.source.position === 'closing')
    .reduce((sum, fatura) => sum + fatura.total, 0);

  const periodDeltaPct =
    priorPeriodTotal > 0 ? ((openTotal - priorPeriodTotal) / priorPeriodTotal) * 100 : null;

  const activeCardCount = cards.filter((card) =>
    card.faturas.some((fatura) => fatura.status !== 'paid'),
  ).length;

  const nextDueFatura = nextCard?.nextDue ?? null;

  return {
    total: faturas.reduce((sum, fatura) => sum + fatura.total, 0),
    openTotal,
    availableLimit: cards.reduce((sum, card) => sum + (card.account.creditAvailableLimit ?? 0), 0),
    nextDue: nextDueFatura?.dueDate
      ? {
          dueDate: nextDueFatura.dueDate,
          fatura: nextDueFatura,
        }
      : null,
    limitUsedPct,
    nextDueDays: nextDueFatura ? daysUntilCalendarDate(nextDueFatura.dueDate, now) : null,
    nextDueAccountName: nextDueFatura?.bank ?? null,
    nextDueAccentColor: nextDueFatura?.bankColor ?? null,
    activeCardCount,
    priorPeriodTotal,
    periodDeltaPct,
  };
}
